import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../components/Layout/Layout';
import Button from '../components/UI/Button';
import ProgressBar from '../components/UI/ProgressBar';
import StatusBadge from '../components/UI/StatusBadge';
import ScoreCard from '../components/UI/ScoreCard';
import { useLanguage } from '../contexts/LanguageContext';
import dataService from '../services/dataService';
import { generateTeacherResultsPDF } from '../utils/pdfExport';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../lib/supabase';

const { FiArrowLeft, FiDownload, FiPrinter, FiExternalLink } = FiIcons;

const Results = () => {
  const { reg_no } = useParams();
  const navigate = useNavigate();
  const { t, isRTL, language, registrationLinks } = useLanguage();
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTeacher = async () => {
      if (!reg_no) {
        setError("No registration number provided");
        setLoading(false);
        return;
      }

      try {
        // First try to get from local storage
        let teacherData = dataService.getTeacherByRegNo(reg_no);

        // If not found in local storage, try Supabase
        if (!teacherData) {
          try {
            console.log("Fetching teacher from Supabase with reg_no:", reg_no);
            const { data, error: supabaseError } = await supabase
              .from('teachers_x7a9b3c4d2')
              .select('*')
              .eq('reg_no', reg_no)
              .maybeSingle();

            if (supabaseError) throw supabaseError;

            if (data) {
              console.log("Teacher found in Supabase:", data);
              // Save to local storage for future reference
              teacherData = dataService.calculateScores(data);
              dataService.saveTeacher(teacherData);
            }
          } catch (supabaseError) {
            console.error("Supabase error:", supabaseError);
          }
        }

        if (teacherData) {
          console.log("Setting teacher data:", teacherData);
          setTeacher(teacherData);
        } else {
          console.log("Teacher not found");
          setError("Teacher not found");
        }
      } catch (err) {
        console.error("Error fetching teacher data:", err);
        setError("Error loading data");
      } finally {
        setLoading(false);
      }
    };

    fetchTeacher();
  }, [reg_no]);

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    if (teacher) {
      const doc = generateTeacherResultsPDF(teacher, t, language);
      doc.save(`${teacher.full_name}_Results.pdf`);
    }
  };

  // Function to get governorate label
  const getGovernorateName = (governorateValue) => {
    if (!governorateValue) return '';
    return t(`governorates.${governorateValue}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-minecraft-green mx-auto mb-4"></div>
            <p className="text-gray-600">{t('loading')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !teacher) {
    return (
      <Layout>
        <div className="max-w-md mx-auto text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-800">{t('noResults')}</p>
            <Button onClick={() => navigate('/')} variant="primary" className="mt-4">
              {t('backToLookup')}
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const categories = [
    { key: 'originality', label: t('originality'), color: 'blue' },
    { key: 'relevance', label: t('relevance'), color: 'green' },
    { key: 'skills', label: t('skills'), color: 'purple' },
    { key: 'presentation', label: t('presentation'), color: 'indigo' },
    { key: 'educational_value', label: t('educationalValue'), color: 'orange' }
  ];

  // Determine which message to display based on status and assignments completion
  // Make sure we have a valid status code - default to FAIL if not
  const statusCode = teacher.status_code || 'FAIL';
  let statusMessage;

  // Check if assignments are not completed
  if (teacher.assignments_completed === false) {
    // Special message for incomplete assignments
    statusMessage = language === 'ar' 
      ? "عزيزي المعلم، لم تقدّم جميع الواجبات المطلوبة لاجتياز هذا التدريب."
      : "Dear Teacher, you did not provide all the assignments required to pass this training.";
  } else {
    // Regular status message
    statusMessage = t(`statusMessages.${statusCode}`);
  }

  // Use the appropriate registration link based on status
  const registrationUrl = registrationLinks[statusCode] || "https://forms.office.com/r/eQaC1bkC59";

  // Determine registration button text based on status
  const registrationBtnText = statusCode === 'TOP_DISTINGUISHED' ? t('registerTeamText') : t('registerAttendText');

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-lg p-6 mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <Button onClick={() => navigate('/')} variant="secondary" size="sm">
              <SafeIcon icon={FiArrowLeft} className="w-4 h-4" />
              <span>{t('backToLookup')}</span>
            </Button>
            <div className="flex space-x-2 rtl:space-x-reverse">
              <Button onClick={handlePrint} variant="secondary" size="sm">
                <SafeIcon icon={FiPrinter} className="w-4 h-4" />
                <span>{t('print')}</span>
              </Button>
              <Button onClick={handleDownloadPDF} variant="primary" size="sm">
                <SafeIcon icon={FiDownload} className="w-4 h-4" />
                <span>{t('saveAsPDF')}</span>
              </Button>
            </div>
          </div>

          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {teacher.full_name}
            </h1>
            <p className="text-lg text-gray-600 mb-4">
              {t('registrationNumber')}: {teacher.reg_no}
            </p>
            {teacher.school && (
              <p className="text-gray-600 mb-2">{teacher.school}</p>
            )}
            {teacher.governorate && (
              <p className="text-gray-600 mb-2">{getGovernorateName(teacher.governorate)}</p>
            )}
            {teacher.city && (
              <p className="text-gray-600">{teacher.city}</p>
            )}
          </div>
        </motion.div>

        {/* Total Score */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-lg p-6 mb-8"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-4 text-center">
            {t('totalScore')}
          </h2>
          <div className="text-center mb-6">
            <div className="text-6xl font-bold text-minecraft-green mb-2">
              {teacher.total_pct}%
            </div>
            <div className="text-2xl text-gray-600">
              {language === 'ar' ? 'من 100' : 'out of 100'}
            </div>
          </div>
          <ProgressBar value={teacher.total_pct} max={100} color="green" className="mb-4" />
          <div className="text-center">
            <StatusBadge status={statusCode} />
          </div>
          {teacher.assignments_completed === false && (
            <div className="mt-4 text-center">
              <div className="inline-block bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium border border-orange-200">
                {t('assignmentsIncomplete')}
              </div>
            </div>
          )}
        </motion.div>

        {/* Category Scores */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow-lg p-6 mb-8"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            {t('categoryScores')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category, index) => (
              <motion.div
                key={category.key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
              >
                <ScoreCard
                  label={category.label}
                  score={teacher[category.key]}
                  maxScore={5}
                  color={category.color}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Status Message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-lg shadow-lg p-6 mb-8"
        >
          <div className="text-center">
            <p className={`text-lg ${teacher.assignments_completed === false ? 'text-orange-700' : 'text-gray-700'} leading-relaxed mb-6`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
              {statusMessage}
            </p>
            <a
              href={registrationUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-minecraft-green hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-minecraft-green"
            >
              <SafeIcon icon={FiExternalLink} className={`w-5 h-5 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
              {registrationBtnText}
            </a>
          </div>
        </motion.div>

        {/* Status Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0 }}
          className="bg-white rounded-lg shadow-lg p-6"
        >
          <div className="text-center">
            <p className="text-lg text-gray-700 leading-relaxed" dir={language === 'ar' ? 'rtl' : 'ltr'}>
              {t(`statusNotes.${statusCode}`)}
            </p>
          </div>
        </motion.div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: white !important;
          }
          .shadow-lg {
            box-shadow: none !important;
            border: 1px solid #e5e7eb !important;
          }
        }
      `}</style>
    </Layout>
  );
};

export default Results;